/** Group key for sidebar lists — product_family alone is ambiguous (box vs device). */
export function templateFamilyGroupKey(
  row: Pick<LabelTemplateListRow, "label_type" | "product_family">,
): string {
  return `${row.label_type ?? ""}\0${row.product_family}`;
}

export type LabelTemplateManagedBy = "label-service" | "hub" | null;

/** Sidebar owner filter buckets (multi-select; empty set = show all). */
export type LabelTemplateOwnerBucket = "hub" | "label-service" | "other";

export const LABEL_TEMPLATE_OWNER_FILTER_CHIPS: ReadonlyArray<{
  bucket: LabelTemplateOwnerBucket;
  label: string;
  activeClass: string;
  inactiveClass: string;
}> = [
  {
    bucket: "hub",
    label: "Hub",
    activeClass: "bg-amber-100 text-amber-900 ring-1 ring-amber-300",
    inactiveClass: "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50",
  },
  {
    bucket: "label-service",
    label: "L-service",
    activeClass: "bg-slate-200 text-slate-800 ring-1 ring-slate-400",
    inactiveClass: "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50",
  },
  {
    bucket: "other",
    label: "Other",
    activeClass: "bg-violet-100 text-violet-900 ring-1 ring-violet-300",
    inactiveClass: "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50",
  },
];

export function templateFamilyOwnerBucket(managedBy: LabelTemplateManagedBy): LabelTemplateOwnerBucket {
  if (managedBy === "hub") return "hub";
  if (managedBy === "label-service") return "label-service";
  return "other";
}

export function templateFamilyMatchesOwnerFilters(
  familyRows: readonly LabelTemplateListRow[],
  activeFilters: ReadonlySet<LabelTemplateOwnerBucket>,
): boolean {
  if (activeFilters.size === 0) return true;
  const primary = templateFamilyPrimaryRow(familyRows);
  if (primary == null) return false;
  return activeFilters.has(templateFamilyOwnerBucket(primary.managed_by));
}

const HUB_HARDCODED_CODE_FAMILIES = new Set([
  "hub-carton-shipment",
  "hub-carton-transfer",
  "hub-kitting-cart",
]);

/** ZPL built in hub TypeScript (not file-based hub-box templates). */
export function templateIsHubHardcodedPrepack(
  row: Pick<LabelTemplateListRow, "managed_by" | "product_family">,
): boolean {
  return row.managed_by === "hub" && HUB_HARDCODED_CODE_FAMILIES.has(row.product_family);
}

export function templateHardcodedSourceHint(productFamily: string): string {
  if (productFamily === "hub-kitting-cart") return "kitting-cart-label-zpl.ts";
  return "prepack-zpl.ts";
}

/** Human label for the Managed by metadata field and sidebar badges. */
export function templateManagedByLabel(managedBy: LabelTemplateManagedBy): string {
  if (managedBy === "label-service") return "Label service";
  if (managedBy === "hub") return "Hub";
  return "Database";
}

/** Git overlays use negative synthetic ids; `public.label_templates` rows have id > 0. */
export function templateIsStoredInSupabase(row: Pick<LabelTemplateListRow, "id">): boolean {
  return row.id > 0;
}

export function templateStorageLabel(row: Pick<LabelTemplateListRow, "id">): "Supabase" | "Git" {
  return templateIsStoredInSupabase(row) ? "Supabase" : "Git";
}

export interface LabelTemplateListRow {
  id: number;
  product_family: string;
  name: string;
  label_type: string | null;
  template: string;
  notes: string | null;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string | null;
  readonly: boolean;
  managed_by: LabelTemplateManagedBy;
  label_size: string | null;
  design_dpi: number | null;
}

/** Active row when present; otherwise the newest version (first in version-desc list). */
export function templateFamilyPrimaryRow(
  familyRows: readonly LabelTemplateListRow[],
): LabelTemplateListRow | null {
  if (familyRows.length === 0) return null;
  return familyRows.find((row) => row.is_active) ?? familyRows[0]!;
}

/** Non-primary versions in the same family (hidden until the group is expanded). */
export function templateFamilyOlderRows(
  familyRows: readonly LabelTemplateListRow[],
): LabelTemplateListRow[] {
  const primary = templateFamilyPrimaryRow(familyRows);
  if (primary == null) return [];
  return familyRows.filter((row) => row.id !== primary.id);
}

/** Rows shown in the sidebar for one family. */
export function templateFamilyVisibleRows(
  familyRows: readonly LabelTemplateListRow[],
  expanded: boolean,
): LabelTemplateListRow[] {
  const primary = templateFamilyPrimaryRow(familyRows);
  if (primary == null) return [];
  if (expanded) return [...familyRows];
  return [primary];
}

/** Hub box labels use label_type `hub-box` (distinct from label-service `box`). */
export const HUB_BOX_LABEL_TYPE = "hub-box";

function stripTemplateNameSuffix(name: string): string {
  return name.replace(/\s+v\d+\s*$/i, "").replace(/\s+label\s*$/i, "").trim();
}

/** Sidebar and detail titles — disambiguate hub-box vs legacy label-service box for the same family. */
export function templateDisambiguatedTitle(
  row: Pick<LabelTemplateListRow, "name" | "label_type" | "product_family">,
): string {
  const base = stripTemplateNameSuffix(row.name);
  const labelType = row.label_type ?? "";
  const family = row.product_family;

  if (labelType === HUB_BOX_LABEL_TYPE) {
    if (/hub box/i.test(base)) return base;
    if (/ box$/i.test(base)) return base.replace(/ box$/i, " hub box");
    return `${family} hub box`;
  }

  if (labelType === "device") {
    if (/device/i.test(base)) return base;
    return `${family} device`;
  }

  if (labelType === "kitting") {
    if (/kitting/i.test(base)) return base;
    return "Picking cart";
  }

  return base;
}

/** Group heading for the sidebar list. */
export function templateGroupTitle(familyRows: readonly LabelTemplateListRow[]): string {
  const reference = templateFamilyPrimaryRow(familyRows);
  if (reference == null) return "";
  return templateDisambiguatedTitle(reference);
}

/** Detail panel title. */
export function templateDetailTitle(row: LabelTemplateListRow): string {
  return templateDisambiguatedTitle(row);
}

/** Sidebar row label — version only because the group header carries the family name. */
export function templateVersionLabel(row: LabelTemplateListRow): string {
  const size = row.label_size?.trim();
  if (size != null && size !== "") return `v${String(row.version)} · ${size}`;
  return `v${String(row.version)}`;
}
